/**
 * Which shape the Integrations section is drawn in — the operator's choice,
 * remembered across reloads.
 *
 * `list` is the DEFAULT and stays the default: it is the denser, more
 * actionable view (endpoints, credentials, the per-vendor connection test). The
 * diagram answers a different question — where does traffic actually go, and
 * what never leaves this box — and an operator who wants that asks for it.
 *
 * Storage throws in a locked-down browser (Safari private mode, blocked
 * third-party storage), and a section that failed to render over a remembered
 * layout preference would be absurd. Both sides swallow, and an unreadable or
 * unrecognised value resolves to the default — same discipline as
 * `theme/color-scheme.ts`.
 */

export type IntegrationsView = 'list' | 'diagram';

/** Namespaced like `nexa.theme` / `nexa.print.format`. */
export const INTEGRATIONS_VIEW_STORAGE_KEY = 'nexa.providers.integrations-view';

export const DEFAULT_INTEGRATIONS_VIEW: IntegrationsView = 'list';

const VIEWS: readonly IntegrationsView[] = ['list', 'diagram'];

export function isIntegrationsView(value: unknown): value is IntegrationsView {
  return typeof value === 'string' && (VIEWS as readonly string[]).includes(value);
}

export function readIntegrationsView(): IntegrationsView {
  try {
    const raw = window.localStorage.getItem(INTEGRATIONS_VIEW_STORAGE_KEY);
    return isIntegrationsView(raw) ? raw : DEFAULT_INTEGRATIONS_VIEW;
  } catch {
    return DEFAULT_INTEGRATIONS_VIEW;
  }
}

export function writeIntegrationsView(view: IntegrationsView): void {
  try {
    window.localStorage.setItem(INTEGRATIONS_VIEW_STORAGE_KEY, view);
  } catch {
    /* the choice holds for this session only */
  }
}
